import { useQueryHelper } from '@anytag/shared-hooks';
import { BaseModal, Button, Icon } from '@anytag/shared-atoms';
import {
  namedOperations,
  TiktokAdAccountInfo,
  useDisconnectTiktokAdAccountMutation,
  useReconnectTiktokAdAccountMutation
} from '@anytag/graphql';

interface DisconnectAdAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDisconnected: boolean;
  onDisconnect: () => void;
  adAccount: TiktokAdAccountInfo;
  hasAccountsLimitWarning?: boolean;
}
export const DisconnectAdAccountModal = ({
  isOpen,
  onClose,
  adAccount,
  onDisconnect,
  isDisconnected,
  hasAccountsLimitWarning
}: DisconnectAdAccountModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { callDisconnectTiktokAdAccount, loading } = useDisconnectTiktokAdAccountMutation({
    refetchQueries: [namedOperations.Query.TiktokBusinessAdAccounts]
  });
  const { callReconnectTiktokAdAccount, loading: isReconnecting } = useReconnectTiktokAdAccountMutation({
    refetchQueries: [namedOperations.Query.TiktokBusinessAdAccounts]
  });

  const handleReconnect = async () => {
    try {
      await callReconnectTiktokAdAccount({
        variables: {
          input: {
            adAccountId: adAccount.id
          }
        }
      });

      enqueueSnackbar(t('succeededInReconnecting', { name: adAccount.name }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  const handleDisconnect = async () => {
    try {
      await callDisconnectTiktokAdAccount({
        variables: {
          input: {
            adAccountId: adAccount.id
          }
        }
      });

      enqueueSnackbar(t('succeededInDisconnecting', { name: adAccount.name }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
    onClose();
  };

  return (
    <>
      {isDisconnected ? (
        <Button
          variant="outline"
          title={t('Button.Reconnect')}
          onClick={handleReconnect}
          loading={isReconnecting}
          className="ml-auto"
          disabled={hasAccountsLimitWarning}
        />
      ) : (
        <Button
          variant="outline"
          title={t('Button.Disconnect')}
          onClick={onDisconnect}
          loading={loading}
          className="ml-auto"
        />
      )}

      <BaseModal
        open={isOpen}
        onClose={onClose}
        title={
          <div className="flex items-center text-xl font-semibold">
            <Icon size={22} icon="warning" variant="yellow" className="mr-2" />
            {t('Dialog.Are you sure to disconnect?')}
          </div>
        }
        description={t('Dialog.This operation cannot be undone, please reconnect if necessary')}
        footer={{
          cancelButton: { onClick: onClose },
          submitButton: { color: 'danger', onClick: handleDisconnect, title: t('Button.Disconnect'), loading }
        }}
      />
    </>
  );
};
